import React from "react";
import { X, ArrowRight, Layers, Pin } from "lucide-react";
import { cn } from "../../lib/utils";
import type { MapHoverPopupSurfaceProps } from "../../contracts/map-workspace";

/**
 * MapHoverPopupSurface is a compact, transient feature identity card.
 * It provides a structured hierarchy optimized for quick scanning while hovering.
 */
export function MapHoverPopupSurface({
  title,
  subtitle,
  badges = [],
  summary = [],
  actions = [],
  onAction,
  onInspect,
  onClear,
  pinned = false,
  className,
}: MapHoverPopupSurfaceProps) {
  return (
    <div
      className={cn(
        "flex flex-col w-64 bg-background border border-border rounded-radius-control shadow-[0_8px_30px_rgb(0,0,0,0.12)] overflow-hidden select-none animate-in fade-in zoom-in duration-150",
        pinned && "ring-1 ring-primary border-primary/30",
        className
      )}
    >
      {/* Identity Block */}
      <div className="flex flex-col bg-muted/20 px-3 py-2.5 border-b border-border/50 relative">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              {pinned && (
                <Pin className="w-3 h-3 text-primary rotate-45 shrink-0" />
              )}
              <h4 className="text-[13px] font-bold leading-tight text-foreground break-words line-clamp-2 tracking-tight">
                {title}
              </h4>
            </div>
            {subtitle && (
              <p className="text-[11px] text-muted-foreground leading-snug break-words line-clamp-2 mt-1 font-medium">
                {subtitle}
              </p>
            )}
          </div>
          
          {onClear && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              className="p-1 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors shrink-0 -mt-1 -mr-1"
              title="Dismiss"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-col p-3 gap-3">
        {/* Badges Row */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {badges.map((badge, i) => (
              <span
                key={i}
                className="px-1.5 py-0.5 rounded-[4px] bg-primary/10 text-primary text-[9px] font-bold uppercase tracking-widest border border-primary/20"
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* Summary Rows */}
        {summary.length > 0 && (
          <div className="flex flex-col gap-1.5">
            {summary.map((item) => (
              <div key={item.key} className="flex items-center justify-between gap-3 group">
                <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider truncate shrink-0">
                  {item.label}
                </span>
                <span className="text-[11px] font-mono font-bold text-foreground truncate text-right">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Action Row */}
        {(actions.length > 0 || onInspect) && (
          <div className="flex flex-col gap-1.5 mt-0.5 border-t border-border/50 pt-2.5">
            {actions.map((action) => (
              <button
                key={action.id}
                disabled={action.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onAction?.(action.id);
                }}
                title={action.disabledReason || undefined}
                className={cn(
                  "flex items-center justify-between w-full px-2.5 py-1.5 rounded-[4px] text-[10px] font-bold transition-all group",
                  "bg-primary text-primary-foreground hover:bg-primary/90 active:scale-[0.98]",
                  "disabled:opacity-50 disabled:bg-muted disabled:text-muted-foreground disabled:active:scale-100 disabled:cursor-not-allowed"
                )}
              >
                <span>{action.label}</span>
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </button>
            ))}
            
            {onInspect && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInspect();
                }}
                className="flex items-center justify-between w-full px-2.5 py-1.5 rounded-[4px] text-[10px] font-bold text-muted-foreground hover:text-foreground hover:bg-muted transition-all border border-border/50"
              >
                <span>Inspect Properties</span>
                <Layers className="w-3.5 h-3.5 opacity-60" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
